import type { ComponentChild, FunctionComponent, MouseEventHandler } from 'preact';
import { useMemo } from 'preact/hooks';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { replaceValue } from 'Core/actions/request';
import { useDispatch } from 'Core/store';
import { FacetValue } from 'Models/index';
import Step from 'Models/step';
import { cloneSafe } from 'Utils/components';
import {
  convertDecimalToImperial,
  getNormBoundsFromRange,
  getRangeFromTerm,
  getRangeFromValue,
  type MinMax,
} from 'Utils/ranged';
import { simpleHandler } from 'Utils/roleHandler';

import type { CommonParams, FacetCommonProps } from '../baseFacet';
import { RangedInputs, Slider } from '../common/index';
import type { Params as RangedInputsParams } from '../common/rangedInputs';

type Params = Omit<CommonParams, 'selection'> & {
  slider: ComponentChild;
  min: number;
  max: number;
  rawMin: string;
  rawMax: string;
  isNullRange: boolean;
  selectedRange: MinMax;
  selectedRawRange: MinMax;
  Inputs: TemplateFunctionInvoker<RangedInputsParams>;
};

const RangedSliderFacet: FunctionComponent<FacetCommonProps<Params>> = ({
  facet,
  field,
  allowEqualBounds,
  templateFunc,
  facetRef,
  commonParams,
  commonRoles,
  config,
}) => {
  const dispatch = useDispatch();
  const rawStep = config.step ?? '1';
  const mode = config.slider?.mode ?? '';
  const step = useMemo(() => new Step(rawStep), [rawStep]);

  const normBounds = getNormBoundsFromRange(facet, step);
  const [minNormBound, maxNormBound] = normBounds.map((m) => +m);
  if (minNormBound === maxNormBound && !allowEqualBounds) {
    return null;
  }

  const selectedTerms: MinMax = facet.selection.length
    ? getRangeFromTerm(facet.selection[0].term)
    : ['*', '*'];
  const selectedNormRange = selectedTerms.map((selectedValue, i) => {
    const value = selectedValue.replace(/^\*$/, String([minNormBound, maxNormBound][i]));
    return step.imperial ? convertDecimalToImperial(value) : value;
  }) as MinMax;

  const rawBounds = getRangeFromValue(facet.values[0].value, facet.values[0].term);
  const selectedValues: MinMax = facet.selection.length
    ? getRangeFromValue(facet.selection[0].value, facet.selection[0].term)
    : ['*', '*'];
  const selectedRawRange = selectedValues.map((selectedValue, i) => {
    const value = selectedValue.replace(/^\*$/, rawBounds[i]);
    return step.imperial ? convertDecimalToImperial(value) : value;
  }) as MinMax;

  function normRangedSelection(values: MinMax, [min, max]: MinMax): MinMax {
    return values.map((value, i) => {
      if (value === '*') {
        return [min, max][i];
      }
      if (+value > +max) {
        return max;
      }
      return +value < +min ? min : value;
    }) as MinMax;
  }
  const slider = (
    <Slider
      field={field}
      bounds={normBounds}
      selected={normRangedSelection(selectedTerms, normBounds)}
      step={step}
      mode={mode}
      key={`slider-for-${field}`}
    />
  );

  function handleCustomRange([minValue, maxValue]: MinMax) {
    const term = FacetValue.createRangedTerm(minValue, maxValue);
    const isSelected = !(minValue === '*' && maxValue === '*');
    dispatch(replaceValue({ term, field, isUnique: true, isSelected }, { mayDiscardValue: true }));
  }
  const Inputs = (templ: TemplateFunction<RangedInputsParams>) =>
    (
      <RangedInputs
        template={templ}
        handleCustomRange={handleCustomRange}
        key={`inputs-for-${field}`}
        bounds={normBounds}
        step={step}
        mode={mode}
        selectedValues={selectedTerms}
      />
    ) as TemplateResult;

  const onClick: MouseEventHandler<HTMLElement> = simpleHandler({ ...commonRoles });

  const component = templateFunc.call({
    ...commonParams,
    slider,
    min: minNormBound,
    max: maxNormBound,
    rawMin: rawBounds[0],
    rawMax: rawBounds[1],
    isNullRange: minNormBound === maxNormBound,
    selectedRange: selectedNormRange,
    selectedRawRange,
    Inputs,
  });
  return cloneSafe(component, facetRef, { onClick });
};

export default RangedSliderFacet;
